import { TierName, TierStatusName } from '@order/graphql';
import type { useLocalizationContext } from '@order/Localization';

import type { AccountSweetpassMembershipSection } from '../../useSweetpassAccountContentfulData';
import type {
  AsyncCta,
  GetSectionProps,
  SweetpassSectionProps,
} from '../types';
import { convertAndFormatDate } from './common';

// ─────────────────────────────────────────────────────────────────────────────

export const getMembershipLevelSection = (
  props: GetMembershipLevelSectionProps,
): SweetpassMembershipLevelSectionProps | undefined => {
  const {
    tier,
    tierStatus,
    hasPaidTierHistory,
    joinDate,
    activeSubscription,
    shouldUseIsoDate,
  } = props;

  const { formattedDate: formattedJoinDate } = convertAndFormatDate(
    shouldUseIsoDate,
    joinDate as string,
  );

  const contentProps = {
    ...props,
    joinDate: formattedJoinDate,
  };

  const hasActiveSubscription = Boolean(activeSubscription);
  const isSweetpass =
    (tier?.name === TierName.SweetpassPlus && !hasActiveSubscription) ||
    tier?.name === TierName.Sweetpass;
  const isSweetpassPlus =
    hasActiveSubscription && tier?.name === TierName.SweetpassPlus;
  const isSweetpassPlusWithFreeTrial =
    isSweetpassPlus && tierStatus === TierStatusName.FreeTrial;

  const isDowngradedSweetpass = isSweetpass && hasPaidTierHistory;
  const isCancelledSweetpassPlus =
    isSweetpassPlus && tierStatus === TierStatusName.CancellationPending;

  if (isCancelledSweetpassPlus) {
    return getMembershipLevelSectionContentForSweetpassPlusCanceled(
      contentProps,
    );
  }

  if (isSweetpassPlusWithFreeTrial) {
    return getMembershipLevelSectionContentForSweetpassPlusTrial(contentProps);
  }

  if (isSweetpassPlus) {
    return getMembershipLevelSectionContentForSweetpassPlusActive(contentProps);
  }

  if (isDowngradedSweetpass) {
    return getMembershipLevelSectionContentForSweetpassDowngraded(contentProps);
  }

  if (isSweetpass) {
    return getMembershipLevelSectionContentForSweetpassInitial(contentProps);
  }
};

// ─── Tier: Sweetpass (Initial) ───────────────────────────────────────────────

const getMembershipLevelSectionContentForSweetpassInitial = (
  props: GetMembershipLevelSectionContentProps,
): SweetpassMembershipLevelSectionProps | undefined => {
  const {
    name,
    membershipDataTitle,
    membershipDataHeader,
    membershipDataBodySweetpass,
    membershipDataCtaSweetpass,
    membershipDataCtaSweetpassFreeTrial,
    planPrice,
    billingFrequencyUnit,
    joinDate,
    isEligibleToFreeTrial,
    freeTrialPlanId = '',
    subscribeHandler,
  } = props;

  const cta = isEligibleToFreeTrial
    ? membershipDataCtaSweetpassFreeTrial
    : membershipDataCtaSweetpass;

  const handler = async () => {
    const planId: string | undefined = isEligibleToFreeTrial
      ? freeTrialPlanId
      : undefined;

    return subscribeHandler(planId);
  };

  return {
    name,
    title: membershipDataTitle,
    headingText: membershipDataHeader.replaceAll(
      '{membershipLevel}',
      MEMBERSHIP_SWEETPASS,
    ),
    bodyText: joinDate
      ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        membershipDataBodySweetpass.replaceAll('{joinDate}', joinDate)
      : '',
    cta: {
      palette: 'limeOnGreen',
      title: cta
        .replaceAll('{planPrice}', `${planPrice}`)
        .replaceAll(
          '{billingFrequencyUnit}',
          billingFrequencyUnit?.toLowerCase(),
        ),
      accessibilityLabel: cta,
      handler,
    },
  };
};

// ─── Tier: Sweetpass (Downgraded) ────────────────────────────────────────────

const getMembershipLevelSectionContentForSweetpassDowngraded = (
  props: GetMembershipLevelSectionContentProps,
): SweetpassMembershipLevelSectionProps | undefined => {
  const {
    name,
    membershipDataTitle,
    membershipDataHeader,
    membershipDataCtaSweetpassFreeTrial,
    membershipDataCtaSweetpassDowngraded,
    planPrice,
    billingFrequencyUnit,
    isEligibleToFreeTrial,
    freeTrialPlanId = '',
    subscribeHandler,
  } = props;

  const cta = isEligibleToFreeTrial
    ? membershipDataCtaSweetpassFreeTrial
    : membershipDataCtaSweetpassDowngraded;

  const handler = async () => {
    const planId: string | undefined = isEligibleToFreeTrial
      ? freeTrialPlanId
      : undefined;

    return subscribeHandler(planId);
  };

  return {
    name,
    title: membershipDataTitle,
    headingText: membershipDataHeader.replaceAll(
      '{membershipLevel}',
      MEMBERSHIP_SWEETPASS,
    ),
    // if there is ever a requirement for a sweetpass downgraded body text, use this.
    // bodyText: membershipDataBodySweetpassDowngraded,
    cta: {
      palette: isEligibleToFreeTrial ? 'limeOnGreen' : 'secondary',
      title: cta
        .replaceAll('{planPrice}', `${planPrice}`)
        .replaceAll(
          '{billingFrequencyUnit}',
          billingFrequencyUnit?.toLowerCase(),
        ),
      accessibilityLabel: cta,
      handler,
    },
  };
};

// ─── Tier: Sweetpass+ (Active) ───────────────────────────────────────────────

const getMembershipLevelSectionContentForSweetpassPlusActive = (
  props: GetMembershipLevelSectionContentProps,
): SweetpassMembershipLevelSectionProps | undefined => {
  const {
    name,
    membershipDataTitle,
    membershipDataHeader,
    membershipDataBodySweetpassPlus,
    activeSubscription,
    shouldUseIsoDate,
  } = props;

  if (!activeSubscription) return undefined;

  const { nextBillingDate = '', nextBillingPeriodAmount = '' } =
    activeSubscription;
  const nextBillingPeriodRecurrence = 'mo';

  const { formattedDate: formattedNextBillingDate } = convertAndFormatDate(
    shouldUseIsoDate,
    nextBillingDate,
  );

  if (!formattedNextBillingDate) return undefined;

  return {
    name,
    title: membershipDataTitle,
    headingText: membershipDataHeader.replaceAll(
      '{membershipLevel}',
      MEMBERSHIP_SWEETPASS_PLUS,
    ),
    bodyText: membershipDataBodySweetpassPlus
      .replaceAll('{nextBillingDate}', formattedNextBillingDate)
      .replaceAll('{nextBillingPeriodAmount}', nextBillingPeriodAmount)
      .replaceAll('{nextBillingPeriodRecurrence}', nextBillingPeriodRecurrence),
  };
};

// ─── Tier: Sweetpass+ (Canceled) ─────────────────────────────────────────────

const getMembershipLevelSectionContentForSweetpassPlusCanceled = (
  props: GetMembershipLevelSectionContentProps,
): SweetpassMembershipLevelSectionProps | undefined => {
  const {
    name,
    membershipDataTitle,
    membershipDataHeader,
    membershipDataBodySweetpassPlusCancelled,
    membershipDataCtaSweetpassPlusCancelled,
    activeSubscription,
    shouldUseIsoDate,
    reactivateSubscriptionHandler,
  } = props;

  if (!activeSubscription) return undefined;

  const { paidThroughDate } = activeSubscription;

  const { formattedDate: formattedPaidThroughDate } = convertAndFormatDate(
    shouldUseIsoDate,
    paidThroughDate as string,
  );

  return {
    name,
    title: membershipDataTitle,
    headingText: membershipDataHeader.replaceAll(
      '{membershipLevel}',
      MEMBERSHIP_SWEETPASS,
    ),
    bodyText: formattedPaidThroughDate
      ? membershipDataBodySweetpassPlusCancelled.replaceAll(
          '{paidThroughDate}',
          formattedPaidThroughDate,
        )
      : undefined,
    cta: {
      palette: 'secondary',
      title: membershipDataCtaSweetpassPlusCancelled,
      accessibilityLabel: membershipDataCtaSweetpassPlusCancelled,
      handler: reactivateSubscriptionHandler,
    },
  };
};

// ─── Tier: Sweetpass+ (Free Trial) ───────────────────────────────────────────

export const getMembershipLevelSectionContentForSweetpassPlusTrial = (
  params: GetMembershipLevelSectionContentForSweetpassPlusTrialProps,
): SweetpassMembershipLevelSectionProps | undefined => {
  const {
    name,
    membershipDataTitle: title,
    membershipDataHeader,
    membershipDataBodySweetpassPlusFreeTrial,
    activeSubscription,
    shouldUseIsoDate,
    formatMessage,
    formatDurationUnitName,
    formatDistanceBetweenDatesInDays,
  } = params;

  if (!activeSubscription) return;

  // ─────────────────────────────────────────────────────────────────────

  const headingText = membershipDataHeader.replaceAll(
    '{membershipLevel}',
    formatMessage(
      'account.sweetpass-membership.membership-level.sweetpass-plus-free-trial.name',
    ),
  );

  // ─────────────────────────────────────────────────────────────────────

  const { price, firstBillingDate, billingFrequencyUnit } = activeSubscription;

  const currentDate = new Date();

  const { dateWithoutTimezone, formattedDate: formattedFirstBillingDate } =
    convertAndFormatDate(shouldUseIsoDate, firstBillingDate as string);

  if (price && formattedFirstBillingDate && billingFrequencyUnit) {
    const localizedBillingFrequencyUnit = formatDurationUnitName(
      billingFrequencyUnit as never,
    );

    const remainingTime = formatDistanceBetweenDatesInDays({
      baseDate: currentDate,
      targetDate: dateWithoutTimezone,
      withoutPrefix: true,
      useMidnight: true,
      minDays: 1,
    });

    const bodyText = membershipDataBodySweetpassPlusFreeTrial
      .replaceAll('{timeRemaining}', remainingTime)
      .replaceAll('{price}', price)
      .replaceAll('{billingFrequencyUnit}', localizedBillingFrequencyUnit)
      .replaceAll('{nextBillingDate}', formattedFirstBillingDate);

    return {
      name,
      title,
      headingText,
      bodyText,
    };
  }

  // ─────────────────────────────────────────────────────────────────────

  return {
    name,
    title,
    headingText,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const MEMBERSHIP_SWEETPASS = 'Sweetpass';
const MEMBERSHIP_SWEETPASS_PLUS = 'Sweetpass+';

// ─── Types ───────────────────────────────────────────────────────────────────

type SweetpassMembershipLevelCta = AsyncCta;

type GetMembershipLevelSectionProps = Readonly<{
  shouldUseIsoDate: boolean;
  autoRenewInfo?: string;
  subscribeHandler: (planId?: string) => Promise<void>;
  reactivateSubscriptionHandler: () => Promise<void>;
  formatDurationUnitName: ReturnType<
    typeof useLocalizationContext
  >['formatDurationUnitName'];
  formatDistanceBetweenDatesInDays: ReturnType<
    typeof useLocalizationContext
  >['formatDistanceBetweenDatesInDays'];
}> &
  AccountSweetpassMembershipSection &
  GetSectionProps;

type GetMembershipLevelSectionContentProps = GetMembershipLevelSectionProps &
  AccountSweetpassMembershipSection &
  Readonly<{
    joinDate: string | null;
  }>;

type GetMembershipLevelSectionContentForSweetpassPlusTrialProps = Pick<
  GetMembershipLevelSectionContentProps,
  | 'name'
  | 'membershipDataTitle'
  | 'membershipDataHeader'
  | 'membershipDataBodySweetpassPlusFreeTrial'
  | 'activeSubscription'
  | 'formatMessage'
  | 'formatDurationUnitName'
  | 'formatDistanceBetweenDatesInDays'
  | 'shouldUseIsoDate'
>;

export type SweetpassMembershipLevelSectionProps = Readonly<{
  name: string;
  title: string;
  headingText?: string;
  bodyText?: string;
  cta?: SweetpassMembershipLevelCta;
}> &
  SweetpassSectionProps;
